import { ETableColumnType, ITableColumn } from '@/interfaces'
import {
  IFacility,
  IPccUploadFile,
  IPccUploadTask,
  IFacilityCredentials
} from '@zunta/configurations'
const groupBy = require('lodash.groupby')

type GroupedTaskList = (
  | IPccUploadTask
  | { id: number; child: boolean; facility: IFacility | number }
)[];

function groupedTaskList (taskList: IPccUploadTask[]) {
  const grouped = groupBy(
    taskList,
    (task: IPccUploadTask) => {
      const facilityCredentials = ((task.facility as IFacility).facilityCredentials || {}) as IFacilityCredentials
      return `${facilityCredentials.username}
      ${facilityCredentials.password}
      ${task.assignedTo as number}`
    }
  )
  const groupedWithParents: GroupedTaskList = []
  Object.keys(grouped).forEach(key => {
    const tasks: IPccUploadTask[] = grouped[key]
    if (!tasks) return
    const parent = tasks[0]
    const children = tasks.filter(task => task !== parent)
    const mappedChildren = children.map(child => {
      return {
        id: child.id,
        child: true,
        facility: child.facility,
        pccUploadFiles: child.pccUploadFiles,
        createdAt: child.createdAt
      }
    })
    groupedWithParents.push(parent)
    mappedChildren.forEach(child => {
      groupedWithParents.push(child)
    })
  })
  return groupedWithParents
}

function getFacilityProperties (
  task: IPccUploadTask,
  open: boolean,
  key: keyof IFacility,
  isChild: boolean = false
) {
  if (!task || isChild) return
  return open ? (task.facility as IFacility)[key] : (task as any)[key]
}

const completedRequestColumns: ITableColumn[] = [
  {
    minWidth: '100px',
    width: 20,
    key: 'assignedTo',
    searchKey: 'assignedTo',
    label: 'Assigned',
    hasSort: true,
    hasSearch: true,
    type: ETableColumnType.default
  },
  {
    minWidth: '250px',
    width: 40,
    key: 'facilityName',
    searchKey: 'facilityName',
    label: 'Facility Name',
    hasSort: true,
    hasSearch: true,
    type: ETableColumnType.default
  },
  {
    minWidth: '200px',
    width: 35,
    key: 'login',
    searchKey: 'login',
    label: 'Log in  & Password',
    hasSort: true,
    hasSearch: true,
    type: ETableColumnType.default
  },
  {
    minWidth: '170px',
    width: 25,
    key: 'updatedAt',
    searchKey: 'updatedAt',
    label: 'Completed At',
    type: ETableColumnType.date,
    hasSearch: true,
  },
  {
    minWidth: '40px',
    width: 10,
    key: 'isMPI',
    searchKey: 'isMPI',
    label: 'Import Type',
    type: ETableColumnType.select,
    hasSearch: true,
    options: [{
      content: 'Crawler',
      key: 'isMPI',
      value: false
    }, {
      content: 'MPI',
      key: 'isMPI',
      value: true
    }]
  },
  {
    minWidth: '130px',
    width: 5,
    key: 'actions',
    hasSearch: false
  }
]

const openRequestColumns: ITableColumn[] = [
  {
    minWidth: '130px',
    width: 12,
    key: 'residentList',
    searchKey: 'residentList',
    label: 'Resident List',
    type: ETableColumnType.default
  },
  {
    minWidth: '130px',
    width: 12,
    key: 'residentContact',
    searchKey: 'residentContact',
    label: 'Resident Contact',
    type: ETableColumnType.default
  },
  {
    minWidth: '130px',
    width: 12,
    key: 'transactionReport',
    searchKey: 'transactionReport',
    label: 'Transaction Report',
    type: ETableColumnType.default
  },
  {
    minWidth: '130px',
    width: 12,
    key: 'elapsedTime',
    searchKey: 'elapsedTime',
    label: 'Time since requested',
    type: ETableColumnType.default
  }
]

function isReportUploaded (
  pccUploadTask: IPccUploadTask,
  type: 'resident' | 'contact' | 'transaction',
  getInvalidReason?: boolean
): string | undefined {
  const pccUploadFiles: IPccUploadFile[] = pccUploadTask.pccUploadFiles as IPccUploadFile[]
  const foundFile = (pccUploadFiles || []).find(
    file => file.reportType === type
  )
  if (foundFile) {
    if (getInvalidReason) {
      return foundFile.invalidReason
    }
    return foundFile.invalidReason ? 'failed' : 'complete'
  } else {
    return 'not-uploaded'
  }
}

export {
  GroupedTaskList,
  groupedTaskList,
  completedRequestColumns,
  openRequestColumns,
  isReportUploaded,
  getFacilityProperties
}
